import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { markActivity, readActivity, forceHeartbeat, broadcast, onBroadcast, runBeforeLogout } from "../lib/session";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const INPUT_EVENTS = ["keydown", "mousedown", "pointerdown", "touchstart", "wheel", "scroll"];
const MOVE_THROTTLE_MS = 5000;

export function SessionGuard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [policy, setPolicy] = useState(null);
    const [left, setLeft] = useState(null); // seconds until logout while warning is shown
    const ending = useRef(false);
    const lastMove = useRef(0);
    const role = user && user.role;

    useEffect(() => {
        if (!role) { setPolicy(null); return; }
        api.get("/auth/session-policy").then(({ data }) => setPolicy(data)).catch(() => setPolicy(null));
    }, [role]);

    const enabled = Boolean(user && policy?.enabled);

    const endSession = async (reason, code, { fromBroadcast = false } = {}) => {
        if (ending.current) return;
        ending.current = true;
        let flush = { ok: true };
        if (reason === "idle" || reason === "signedout") flush = await runBeforeLogout();
        const q = new URLSearchParams({ reason, returnTo: `${loc.pathname}${loc.search}` });
        if (role === "partner") q.set("partner", "1"); else if (role !== "patient" && role !== "caregiver") q.set("internal", "1");
        if (!flush.ok) q.set("unsaved", "1");
        if (!fromBroadcast) broadcast({ type: "logout", reason });
        setLeft(null);
        // RoleRoute reads this flag if it renders user=false before the router transition lands → still ends on /signin with the notice.
        sessionStorage.setItem("vien_session_end", JSON.stringify({ reason, returnTo: `${loc.pathname}${loc.search}`, unsaved: !flush.ok }));
        nav(`/signin?${q.toString()}`, { replace: true });
        // Each tab holds its own server session (sessionStorage token) → every tab revokes its own sid; broadcast receivers never re-broadcast.
        await logout(reason === "idle" ? "idle" : reason === "signedout" ? "signedout" : "user", { server: !["SESSION_IDLE", "SESSION_REVOKED", "SESSION_LEGACY"].includes(code) });
        setTimeout(() => { ending.current = false; }, 500);
    };

    // Server told us the session is gone (idle/revoked/legacy) → never keep a dead token around.
    useEffect(() => {
        const h = (e) => { if (user) endSession("expired", e.detail); };
        window.addEventListener("vien:session-ended", h);
        return () => window.removeEventListener("vien:session-ended", h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loc.pathname]);

    // Cross-tab: logout anywhere logs out everywhere.
    useEffect(() => {
        if (!user) return undefined;
        return onBroadcast((m) => { if (m?.type === "logout") endSession(m.reason === "idle" ? "idle" : "signedout", null, { fromBroadcast: true }); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, loc.pathname]);

    // Genuine input only. Mouse-move is throttled; background requests never touch the timer.
    useEffect(() => {
        if (!enabled) return undefined;
        markActivity();
        const onInput = () => markActivity();
        const onMove = () => { const n = Date.now(); if (n - lastMove.current > MOVE_THROTTLE_MS) { lastMove.current = n; markActivity(n); } };
        INPUT_EVENTS.forEach((ev) => window.addEventListener(ev, onInput, { passive: true, capture: true }));
        window.addEventListener("mousemove", onMove, { passive: true });
        return () => { INPUT_EVENTS.forEach((ev) => window.removeEventListener(ev, onInput, { capture: true })); window.removeEventListener("mousemove", onMove); };
    }, [enabled]);
    useEffect(() => { if (enabled) markActivity(); }, [enabled, loc.pathname]); // navigation counts

    // 1 s tick reads the shared timestamp (localStorage) so activity in any tab keeps all tabs alive.
    useEffect(() => {
        if (!enabled) { setLeft(null); return undefined; }
        const idleMs = policy.idle_minutes * 60000, warnMs = policy.warn_seconds * 1000;
        const id = setInterval(() => {
            if (!readActivity()) { markActivity(); return; } // key missing (first run / cleared) → start the clock now, never treat as idle
            const idle = Date.now() - readActivity();
            if (idle >= idleMs) { endSession("idle"); return; }
            setLeft(idle >= idleMs - warnMs ? Math.ceil((idleMs - idle) / 1000) : null);
        }, 1000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, policy, loc.pathname]);

    if (!enabled || left == null) return null;
    return (
        <Dialog open onOpenChange={() => {}}>
            <DialogContent className="max-w-sm" data-testid="idle-warning-dialog" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-600" /> Still there?</DialogTitle>
                    <DialogDescription>
                        For patient privacy you will be signed out in <span className="font-mono font-bold text-slate-900 dark:text-white" data-testid="idle-countdown">{left}s</span>. Unsaved notes are saved first.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" data-testid="idle-signout-now" onClick={() => endSession("idle")}>Sign out now</Button>
                    <Button data-testid="idle-stay-signed-in" autoFocus onClick={() => { forceHeartbeat(); setLeft(null); }}>Stay signed in</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
